import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { tenantFileService, TenantFile, StorageInfo, FileListParams } from '@/services/tenantFileService';

interface FileFilters {
  category: string | null;
  mimeType: string | null;
  search: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  sortBy: 'created_at' | 'name' | 'size_bytes';
  sortDir: 'asc' | 'desc';
}

interface Pagination {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

interface TenantFileState {
  files: TenantFile[];
  currentFile: TenantFile | null;
  storageInfo: StorageInfo | null;
  filters: FileFilters;
  pagination: Pagination;
  loading: boolean;
  uploading: boolean;
  error: any | null;
}

const initialState: TenantFileState = {
  files: [],
  currentFile: null,
  storageInfo: null,
  filters: {
    category: null,
    mimeType: null,
    search: null,
    dateFrom: null,
    dateTo: null,
    sortBy: 'created_at',
    sortDir: 'desc',
  },
  pagination: {
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0,
  },
  loading: false,
  uploading: false,
  error: null,
};

export const fetchFiles = createAsyncThunk(
  'tenantFile/fetchFiles',
  async (page: number | undefined, { getState, rejectWithValue }) => {
    const state = getState() as { tenantFile: TenantFileState };
    const filters = state.tenantFile.filters;
    const pagination = state.tenantFile.pagination;

    const params: Partial<FileListParams> = {
      ...filters,
      page: page ?? pagination.currentPage,
      perPage: pagination.perPage,
    } as any;

    Object.keys(params).forEach((key) => {
      if ((params as any)[key] === null || (params as any)[key] === undefined) {
        delete (params as any)[key];
      }
    });

    try {
      const response = await tenantFileService.listFiles(params as FileListParams);
      return response.data?.data ?? response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchFile = createAsyncThunk(
  'tenantFile/fetchFile',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await tenantFileService.getFile(id);
      return response.data?.data ?? response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const uploadFiles = createAsyncThunk(
  'tenantFile/uploadFiles',
  async ({ formData, onUploadProgress }: { formData: FormData; onUploadProgress?: (progress: any) => void }, { dispatch, rejectWithValue }) => {
    try {
      const response = await tenantFileService.uploadFiles(formData, onUploadProgress);
      dispatch(fetchFiles(1));
      dispatch(fetchStorageInfo());
      return response.data?.data ?? response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteFile = createAsyncThunk(
  'tenantFile/deleteFile',
  async (id: number, { dispatch, rejectWithValue }) => {
    try {
      await tenantFileService.deleteFile(id);
      dispatch(fetchStorageInfo());
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const bulkDelete = createAsyncThunk(
  'tenantFile/bulkDelete',
  async (ids: number[], { dispatch, rejectWithValue }) => {
    try {
      const response = await tenantFileService.bulkDelete(ids);
      dispatch(fetchStorageInfo());
      return { ids, deletedCount: response.data?.data?.deletedCount ?? 0 };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const restoreFile = createAsyncThunk(
  'tenantFile/restoreFile',
  async (id: number, { dispatch, rejectWithValue }) => {
    try {
      await tenantFileService.restoreFile(id);
      dispatch(fetchFiles(undefined));
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchStorageInfo = createAsyncThunk(
  'tenantFile/fetchStorageInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await tenantFileService.getStorageInfo();
      return response.data?.data ?? response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const tenantFileSlice = createSlice({
  name: 'tenantFile',
  initialState,
  reducers: {
    setError(state, action: PayloadAction<any>) {
      state.error = action.payload;
      state.loading = false;
      state.uploading = false;
    },
    clearError(state) {
      state.error = null;
    },
    setFilters(state, action: PayloadAction<Partial<FileFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters(state) {
      state.filters = {
        category: null,
        mimeType: null,
        search: null,
        dateFrom: null,
        dateTo: null,
        sortBy: 'created_at',
        sortDir: 'desc',
      };
      state.pagination.currentPage = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload;
    },
    reset() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchFiles
      .addCase(fetchFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload ?? {};
        state.files = payload.data ?? [];
        state.pagination = {
          currentPage: payload.currentPage ?? 1,
          lastPage: payload.lastPage ?? 1,
          perPage: payload.perPage ?? 20,
          total: payload.total ?? 0,
        };
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchFile
      .addCase(fetchFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFile.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFile = action.payload;
      })
      .addCase(fetchFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // uploadFiles
      .addCase(uploadFiles.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadFiles.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(uploadFiles.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      })
      // deleteFile
      .addCase(deleteFile.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        const nextFiles = state.files.filter((f) => f.id !== action.payload);
        if (nextFiles.length !== state.files.length) {
          // Clamp: an out-of-sync store (a file deleted elsewhere, or a repeated
          // fulfilled) used to drive the counter negative.
          state.pagination.total = Math.max(0, state.pagination.total - 1);
        }
        state.files = nextFiles;
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.error = action.payload;
      })
      // bulkDelete
      .addCase(bulkDelete.pending, (state) => {
        state.error = null;
      })
      .addCase(bulkDelete.fulfilled, (state, action) => {
        state.files = state.files.filter((f) => !action.payload.ids.includes(f.id));
        // `deletedCount` is server-reported and can exceed the locally known
        // count, so clamp instead of letting the total go negative.
        state.pagination.total = Math.max(
          0,
          state.pagination.total - (action.payload.deletedCount || 0),
        );
      })
      .addCase(bulkDelete.rejected, (state, action) => {
        state.error = action.payload;
      })
      // fetchStorageInfo
      .addCase(fetchStorageInfo.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchStorageInfo.fulfilled, (state, action) => {
        state.storageInfo = action.payload;
      })
      .addCase(fetchStorageInfo.rejected, (state, action) => {
        // Without this a failed quota request was completely unobserved.
        state.error = action.payload;
      });
  },
});

export const { setError, clearError, setFilters, clearFilters, setPage, reset } = tenantFileSlice.actions;

export default tenantFileSlice.reducer;

// Selects the tenantFile slice (NOT the root state). Typed as `state: any`
// because the only consumer (views/app/pages/files/index.tsx) also reads
// derived fields (`isLoading`, `hasFiles`) that are not part of
// TenantFileState; typing the return value would surface pre-existing
// consumer errors. See report follow-up.
export const selectTenantFileStore = (state: any) => state.tenantFile;

